import React, { useState, useRef, useEffect, useCallback } from "react";
import type { IconType } from "react-icons";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

interface Option {
  label: string;
  value: string;
}

interface SelectInputProps {
  name: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  icon?: IconType;
  disabled?: boolean;
}

const SelectInput: React.FC<SelectInputProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  required = false,
  error = false,
  errorMessage = "Field is required",
  icon: Icon,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = useCallback(
    () => options.find((opt) => opt.value === value)?.label || placeholder,
    [options, value, placeholder]
  );

  return (
    <div className="phoneNumberContainer" ref={wrapperRef}>
      <label className="textInputLabel">
        {label}
        {required && <span className="textInputLabelRequired">*</span>}
      </label>

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`selectButton ${
          error ? "error" : "hover"
        } ${disabled ? "disabled" : "active"} bg-white`}
        aria-expanded={isOpen}
        aria-controls="select-options"
      >
        <div className="selectedContainer">
          {Icon && <Icon className="selectedContainerIcon" size={18} />}
          <span className={`${!value && ""}`}>
            {selectedLabel()}
          </span>
        </div>
        <div className="selectLeftButton">
          {isOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
        </div>
      </button>

      {isOpen && (
        <ul
          id="select-options"
          className="selectOptionsContainer"
          role="listbox"
        >
          {options.map((option) => (
            <li
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              role="option"
              className={`selectOptions ${
                option.value === value ? "selected" : ""
              }`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="textInputErrorMessage">{errorMessage}</p>}
    </div>
  );
};

export default SelectInput;